import './Header.scss';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ContextualMenu } from 'office-ui-fabric-react/lib/ContextualMenu';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import {
    ISharingItemInformation, ClientId, Mode, ISharingInformation,
    AccessStatus, ISharingLinkSettings, ISharingStore
} from '../../interfaces/SharingInterfaces';
import { ShareViewState } from '../Share/Share';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import * as ClientIdHelper from '../../utilities/ClientIdHelper';
import * as ShareHelper from '../../utilities/ShareHelper';
import stringsResx from '../Share.resx';
import PlatformDetection from '@ms/odsp-utilities/lib/browser/PlatformDetection';
import ResponsiveUI from '@ms/odsp-utilities/lib/browser/ResponsiveUI';
import { createRef } from 'office-ui-fabric-react/lib/Utilities';

export interface IHeaderProps {
    clientId?: ClientId;
    ctaLabel?: string;
    currentSettings?: ISharingLinkSettings;
    errorMessage?: JSX.Element;
    hideShareDialogOverflowButton?: boolean;
    item?: ISharingItemInformation;
    maximumMessageLength?: number;
    maximumSubjectLength?: number;
    messageText?: string;
    subjectText?: string;
    mode?: Mode;
    onManageExistingAccessClick?: () => void; // Not applicable to Headers that don't show the more button.
    onSendLinkClick?: (message: string, subject?: string) => void;
    linkRecipients?: Array<IPerson>;
    permissionsMap?: { [index: string]: AccessStatus };
    sharingInformation?: ISharingInformation;
    viewState: ShareViewState;
    closeButtonOnBlur?: () => void; // to fix issue that Android doesn't read focus close button.
    closeButtonOnFocus?: () => void;
}

export interface IHeaderState {
    showContextualMenu: boolean;
    target: HTMLDivElement;
}

export class Header extends React.Component<IHeaderProps, IHeaderState> {
    private _onDismiss: () => void;
    private _platformDetection: PlatformDetection;
    private _breakpoint: number;
    private _closeButton = createRef<HTMLElement>();
    private _store: ISharingStore;

    static contextTypes = {
        onDismiss: PropTypes.func,
        platformDetection: PropTypes.object.isRequired,
        breakpoint: PropTypes.object.isRequired,
        sharingStore: PropTypes.object.isRequired
    };

    constructor(props: IHeaderProps, context: any) {
        super(props);

        this.state = {
            showContextualMenu: false,
            target: null
        };

        this._onDismiss = context.onDismiss;
        this._platformDetection = context.platformDetection;
        this._breakpoint = context.breakpoint;
        this._store = context.sharingStore;

        this._onDismissMoreContextualMenu = this._onDismissMoreContextualMenu.bind(this);
        this._onManageExistingAccessClick = this._onManageExistingAccessClick.bind(this);
        this._onSendLinkClick = this._onSendLinkClick.bind(this);
        this._onMoreClick = this._onMoreClick.bind(this);
    }

    render(): React.ReactElement<{}> {
        const props = this.props;

        if (props.mode === Mode.linkSettings) {
            return (
                <h3 className='od-ShareHeader-linkSettings'>
                    { this._getViewName() }
                    <div className='od-ShareHeader-buttons-right'>
                        { this._renderCloseButton() }
                    </div>
                </h3>
            );
        }
        const isTitleHidden = props.viewState === ShareViewState.linkSuccess || props.viewState === ShareViewState.error || props.viewState === ShareViewState.notify;
        const isMobile = ClientIdHelper.isMobile(this.props.clientId) || this._platformDetection.isMobile;
        const isTablet = isMobile && this._breakpoint >= ResponsiveUI.DEFAULT_BREAKPOINTS.Large;
        const backgroundColorClass = !isTitleHidden ? ' od-ShareHeader-backgroundColor' : '';
        const title = !isTitleHidden ?
            (
                <div className={ 'od-ShareHeader' + backgroundColorClass }>
                    <div className='od-ShareHeader-title'>
                        <h1 className='od-ShareHeader-viewName'>{ this._getViewName() }</h1>
                        { this._renderItemName() }
                    </div>
                </div>
            ) : '';
        const leftHeaderButtons = isMobile ?
            (
                <div className='od-ShareHeader-buttons-left'>
                    { this._renderCloseButton() }
                </div>
            ) : '';

        return (
            <div>
                { leftHeaderButtons }
                { title }
                { this._renderRightHeaderButtons(isMobile, isTablet, isTitleHidden, props.viewState) }
                { this._renderContextualMenu() }
            </div>
        );
    }

    private _renderMoreButton() {
        if (this.props.viewState === ShareViewState.default && !this.props.hideShareDialogOverflowButton && !this._store.isBundleShare()) {
            return (
                <button
                    className='od-ShareHeader-button'
                    aria-label={ stringsResx.moreOptionsLabel }
                    onClick={ this._onMoreClick }
                    aria-haspopup={ true }
                    aria-expanded={ this.state.showContextualMenu }
                    title={ stringsResx.moreOptionsLabel }
                    role='button'
                >
                    <Icon iconName='More' />
                </button>
            );
        }
    }

    private _renderCloseButton() {
        if (this._onDismiss && ClientIdHelper.showCloseButton(this.props.clientId) && this.props.viewState !== ShareViewState.permissionsList) {
            return (
                <button
                    className='od-ShareHeader-button'
                    aria-label={ stringsResx.closeButtonLabel }
                    onClick={ this._dismissComponent }
                    ref={ this._closeButton }
                    onBlur={ this.props.closeButtonOnBlur }
                    title={ stringsResx.closeButtonLabel }
                >
                    <Icon iconName='Cancel' />
                </button>
            );
        }
    }

    @autobind
    private _dismissComponent(ev: React.MouseEvent<any>) {
        ev.stopPropagation();
        ev.preventDefault();
        this._onDismiss();
    }

    private _renderRightHeaderButtons(isMobile: boolean, isTablet: boolean, isTitleHidden: boolean, shareViewState: ShareViewState) {
        if (isMobile && !isTitleHidden && shareViewState === ShareViewState.default) {
            return (
                <div className='od-ShareHeader-buttons-right'>
                    { this._renderMoreButton() }
                </div>
            );
        } else if (isMobile && (isTitleHidden || shareViewState !== ShareViewState.default)) {
            return '';
        } else {
            return (
                <div className='od-ShareHeader-buttons-right'>
                    { this._renderMoreButton() }
                    { this._renderCloseButton() }
                </div>
            );
        }
    }

    private _renderItemName() {
        const props = this.props;
        const item = props.item;

        if (!item) {
            return;
        }

        const folderInfo = item.childCount > 1 ? ` (${StringHelper.format(stringsResx.folderHeader, item.childCount)})` : '';

        if (ClientIdHelper.hideItemName(props.clientId, this._platformDetection)) {
            return;
        } else {
            return (
                <div className='od-ShareHeader-itemName'>{ ShareHelper.truncateItemNameForHeader(`${item.name} ${folderInfo}`) }</div>
            );
        }
    }

    private _renderContextualMenu() {
        const items = [];

        // Option is available in all cases.
        items.push({
            key: 'editable',
            name: stringsResx.manageExistingAccessLabel,
            onClick: this._onManageExistingAccessClick
        });

        if (this.state.showContextualMenu) {
            return (
                <ContextualMenu
                    ariaLabel={ stringsResx.moreOptionsLabel }
                    className='od-ShareHeader-moreContextualMenu'
                    items={ items }
                    onDismiss={ this._onDismissMoreContextualMenu }
                    target={ this.state.target }
                    isBeakVisible={ true }
                />
            );
        }
    }

    private _onMoreClick(ev: React.MouseEvent<any>) {
        this.setState({
            showContextualMenu: !this.state.showContextualMenu,
            target: ev.nativeEvent.target as HTMLDivElement
        });
    }

    private _onDismissMoreContextualMenu() {
        this.setState({
            ...this.state,
            showContextualMenu: false
        });
    }

    private _onManageExistingAccessClick() {
        this.props.onManageExistingAccessClick();
    }

    private _onSendLinkClick(ev: React.MouseEvent<any>) {
        this.props.onSendLinkClick(this.props.messageText, this.props.subjectText);
    }

    private _getViewName() {
        const strings = stringsResx;

        switch (this.props.viewState) {
            case ShareViewState.default:
                return strings.shareLinkHeader;
            case ShareViewState.modifyPermissions:
                return strings.modifyPermissionsHeader;
            case ShareViewState.permissionsList:
                return strings.manageExistingAccessLabel;
            case ShareViewState.policyDetails:
                return strings.policyDetailsHeader;
            case ShareViewState.grantPermissions:
                return strings.grantAccessPageLabel;
            case ShareViewState.addPeople:
                return strings.addPeopleLabel;
            case ShareViewState.addPeopleRequestAccess:
                return strings.requestAccessLabel;
            default:
                return strings.shareLinkHeader;
        }
    }

    public focusCloseButton(): void {
        if (this._closeButton.current) {
            this._closeButton.current.focus();
            this.props.closeButtonOnFocus && this.props.closeButtonOnFocus();
        }
    }
}